// apps/web/src/features/dashai/lib/dsfTableClient.ts
//
// Shared client for the dashaiDsfTable Firebase function. Wraps
// `postWithAuthRetry` and returns a `{ data, error }` envelope.
//
// 14 modes are supported by the backend (see
// `firebase/functions/src/workflows/dsf-table/`). Callers stay
// mode-agnostic — they pass `body.mode` through and read `data.success`.

import { postWithAuthRetry } from './onePSfaClient';

export interface DsfTableInvokeResult<T = unknown> {
  data: T | null;
  error: Error | null;
}

/**
 * Returns the dashaiDsfTable Firebase function URL.
 *
 * Resolution order:
 *   1. `VITE_DSF_TABLE_URL` — explicit override for local emulator / one-off
 *      diversion (matches the convention used by every other Firebase client
 *      in this dir).
 *   2. `${VITE_FIREBASE_FUNCTIONS_URL}/dashaiDsfTable` — derived from the
 *      standard per-env Firebase Functions base URL set on dev / stg / prod
 *      `.env.*` files.
 *   3. `undefined` — caller should error / fall back. We return undefined
 *      rather than throwing so `getDsfTableUrl()` is safe to call during
 *      module init.
 */
export function getDsfTableUrl(): string | undefined {
  const explicit = import.meta.env.VITE_DSF_TABLE_URL as string | undefined;
  if (explicit) return explicit;
  const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
  if (base) return `${base.replace(/\/+$/, '')}/dashaiDsfTable`;
  return undefined;
}

/**
 * Invoke the dashaiDsfTable Firebase function with the given body. Returns
 * a `{ data, error }` envelope:
 *
 *   - On 2xx: `{ data: <parsed JSON>, error: null }`.
 *   - On non-2xx: `{ data: null, error: Error(message) }` — `message`
 *     contains the response body when available, else `HTTP <status>`.
 *   - On network failure: `{ data: null, error: Error(<network error>) }`.
 *   - When `VITE_FIREBASE_FUNCTIONS_URL` is unset: `{ data: null, error:
 *     Error('dashaiDsfTable function URL not configured') }`.
 *
 * No fallback path. The dsf_table backend is read-only against
 * ClickHouse. A regression on Firebase is fix-forward or PR-revert, not a
 * silent fallback.
 */
export async function invokeDsfTable<T = unknown>(
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<DsfTableInvokeResult<T>> {
  const url = getDsfTableUrl();
  if (!url) {
    return { data: null, error: new Error('dashaiDsfTable function URL not configured') };
  }
  try {
    const res = await postWithAuthRetry(url, body, signal);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return {
        data: null,
        error: Object.assign(new Error(text || `HTTP ${res.status}`), { status: res.status }),
      };
    }
    const data = (await res.json()) as T;
    return { data, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { data: null, error: new Error(message) };
  }
}

/**
 * Streaming variant for CSV-export modes (e.g.
 * `multi-franchise-compliance-export`). Returns the raw `Response` so
 * callers can iterate the body via `res.body!.getReader()` and trigger a
 * Blob download. Throws if the function URL is unset or the response is
 * non-2xx — the streaming code paths already have their own error UI.
 */
export async function streamDsfTable(
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Response> {
  const url = getDsfTableUrl();
  if (!url) throw new Error('dashaiDsfTable function URL not configured');
  const res = await postWithAuthRetry(url, body, signal);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `dashaiDsfTable stream failed (HTTP ${res.status})`);
  }
  return res;
}
